import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';
import { useIsMobile } from '@/hooks/useMediaQuery';

const DEBOUNCE_MS = 500;

export function NotesPanel() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const notesPanelNodeId = useEditorStore((s) => s.notesPanelNodeId);
  const closeNotesPanel = useEditorStore((s) => s.closeNotesPanel);
  const nodes = useDocumentStore((s) => s.nodes);
  const mapDoc = useDocumentStore((s) => s.mapDoc);

  const node = notesPanelNodeId ? nodes.get(notesPanelNodeId) : undefined;

  const [localNotes, setLocalNotes] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync local state when the panel opens or the node changes
  useEffect(() => {
    if (node) {
      setLocalNotes(node.notes);
    }
  }, [notesPanelNodeId, node?.notes]);

  // Focus textarea when panel opens
  useEffect(() => {
    if (notesPanelNodeId && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [notesPanelNodeId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setLocalNotes(value);

      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (notesPanelNodeId) {
          mapDoc.updateNodeNotes(notesPanelNodeId, value);
        }
      }, DEBOUNCE_MS);
    },
    [notesPanelNodeId, mapDoc],
  );

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        // Flush any pending save
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
          if (notesPanelNodeId) {
            mapDoc.updateNodeNotes(notesPanelNodeId, localNotes);
          }
        }
        closeNotesPanel();
      }
    },
    [closeNotesPanel, notesPanelNodeId, mapDoc, localNotes],
  );

  const handleClose = useCallback(() => {
    // Flush any pending save
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      if (notesPanelNodeId) {
        mapDoc.updateNodeNotes(notesPanelNodeId, localNotes);
      }
    }
    closeNotesPanel();
  }, [closeNotesPanel, notesPanelNodeId, mapDoc, localNotes]);

  if (!notesPanelNodeId || !node) return null;

  const panelStyle: React.CSSProperties = {
    position: isMobile ? 'fixed' : 'absolute',
    top: isMobile ? 0 : 50,
    bottom: isMobile ? 0 : 28,
    right: 0,
    width: isMobile ? '100%' : 300,
    zIndex: isMobile ? 35 : 20,
    background: 'var(--bg-app)',
    borderLeft: isMobile ? 'none' : '1px solid var(--border-subtle)',
    fontFamily: 'var(--font-family)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'notesPanelSlideIn 150ms ease-out',
  };

  return (
    <>
    {isMobile && (
      <div
        onClick={closeNotesPanel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--bg-overlay)',
          zIndex: 34,
        }}
      />
    )}
    <div
      style={panelStyle}
      onKeyDown={handleKeyDown}
    >
      <style>{`
        @keyframes notesPanelSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          paddingTop: isMobile ? 'var(--space-4)' : 12,
          borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            color: 'var(--text-secondary)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('panels.notes')}
        </span>
        <button
          onClick={handleClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-lg)',
            padding: '0 4px',
            lineHeight: 1,
          }}
          title={t('panels.closePanel', { panel: 'notes' })}
        >
          x
        </button>
      </div>

      {/* Node title */}
      <div
        style={{
          padding: '8px 16px',
          color: 'var(--text-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {node.text || t('panels.emptyNode')}
      </div>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={localNotes}
        onChange={handleChange}
        placeholder={t('panels.addNotes')}
        style={{
          flex: 1,
          margin: '0 16px',
          padding: 12,
          background: 'var(--bg-surface)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-default)',
          borderRadius: 6,
          resize: 'none',
          fontFamily: 'var(--font-family)',
          fontSize: 'var(--font-size-sm)',
          lineHeight: 1.5,
          outline: 'none',
        }}
      />

      {/* Character count */}
      <div
        style={{
          padding: '8px 16px',
          color: 'var(--text-tertiary)',
          fontSize: 'var(--font-size-xs)',
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {t('panels.characterCount', { count: localNotes.length })}
      </div>
    </div>
    </>
  );
}
